/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { VisuallyHidden } from '@coursera/cds-core';

import type { Props as ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessage';
import ChatMessageBody from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageBody';
import ChatMessageFooter from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageFooter';
import ChatRecommendations from 'bundles/ai-coach-platform/components/patterns/chat/recommendations/ChatRecommendations';
import {
  isCoachMessage as isCoachMessageUtil,
  isErrorMessage as isErrorMessageUtil,
  isTextMessage as isTextMessageUtil,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';
import CoachRecommendedProductCards from 'bundles/unified-common/components/coach-on-discovery/CoachRecommendedProductCards';

import _t from 'i18n!nls/unified-common';

const styles = {
  coachMessageBody: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-100) 0 var(--cds-spacing-100);
  `,
  learnerMessageBody: css`
    padding: var(--cds-spacing-100);
    background: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-100);
    max-width: 90%;
    margin-left: auto;

    p {
      margin-bottom: 0;
    }

    p + p {
      margin-top: var(--cds-spacing-50);
    }
  `,
};

const CourseraAiMessage = ({
  message,
  index,
  messages,
  sendMessage,
  coachPersona,
  learnerPersona,
  children,
  searchTerm,
}: ChatMessageProps) => {
  const { sender } = message;

  const isLastMessage = index === messages.length - 1;
  const isCoachMessage = isCoachMessageUtil(sender);
  const persona = isCoachMessage ? coachPersona : learnerPersona;

  // only allow Coach text messages to be copied
  const showCopyMessage = isCoachMessage && isTextMessageUtil(message);
  const isErrorMessage = isCoachMessage && isErrorMessageUtil(message);

  return (
    <>
      <VisuallyHidden>{isCoachMessage ? _t('Coursera:') : persona.name}</VisuallyHidden>

      <ChatMessageBody
        message={message}
        searchTerm={searchTerm}
        css={isCoachMessage ? styles.coachMessageBody : styles.learnerMessageBody}
      >
        {isCoachMessage ? (
          <>
            <CoachRecommendedProductCards productCards={message.productCards} />
            <ChatRecommendations message={message} sendMessage={sendMessage} showRecommendedActions={isLastMessage} />
          </>
        ) : null}
        {children}
      </ChatMessageBody>

      {/* hide message actions for Coach error messages */}
      {isCoachMessage && !isErrorMessage && <ChatMessageFooter showCopyMessage={showCopyMessage} message={message} />}
    </>
  );
};

export default CourseraAiMessage;
